/**
 * PerformanceCampaignDetail — Iter Feb 2026 R41.
 *
 * Drill-down: campaign → adsets → ads → creative + breakdown insights.
 *
 * Layout:
 *   Top:      Campaign header + 7d KPI strip
 *   Middle:   Adsets list (click to expand ads grid)
 *   Right:    Selected ad → creative preview (iframed HTML) + fatigue banner
 *   Bottom:   Breakdown tabs (Age, Gender, Placement, Device, Region, Hour)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, PlayCircle, PauseCircle, ChevronRight, ChevronDown,
  Sparkles, AlertTriangle, DollarSign, MousePointerClick, Target,
  Users, MonitorSmartphone, MapPin, Clock3, Image as ImageIcon, Flame,
} from "lucide-react";

const BREAKDOWNS = [
  { key: "age",       label: "Age",       icon: Users },
  { key: "gender",    label: "Gender",    icon: Users },
  { key: "placement", label: "Placement", icon: MonitorSmartphone },
  { key: "device",    label: "Device",    icon: MonitorSmartphone },
  { key: "region",    label: "Region",    icon: MapPin },
  { key: "hour",      label: "Hour",      icon: Clock3 },
];

const fmtMoney = (v) => typeof v === "number" ? `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—";
const fmtInt = (v) => typeof v === "number" ? v.toLocaleString("en-IN") : "—";
const fmtPct = (v) => typeof v === "number" ? `${v.toFixed(2)}%` : "—";

export default function PerformanceCampaignDetail() {
  const { activeWorkspaceId } = useAuth();
  const { cid } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [adsets, setAdsets] = useState([]);
  const [expanded, setExpanded] = useState({}); // adset_id → true
  const [adsByAdset, setAdsByAdset] = useState({});
  const [selectedAd, setSelectedAd] = useState(null); // full ad detail
  const [breakdownKey, setBreakdownKey] = useState("placement");
  const [breakdownData, setBreakdownData] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  // Initial load: adsets under this campaign
  const load = useCallback(async () => {
    if (!activeWorkspaceId || !cid) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/campaigns/${cid}/adsets`);
      setCampaign(data.campaign);
      setAdsets(data.adsets || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load campaign")); }
  }, [activeWorkspaceId, cid]);
  useEffect(() => { load(); }, [load]);

  // Load breakdown when key or campaign changes
  useEffect(() => {
    if (!campaign) return;
    setBreakdownLoading(true);
    api.get(`/workspaces/${activeWorkspaceId}/perf/insights/breakdown`, {
      params: { entity_type: "campaign", entity_id: cid, breakdown: breakdownKey },
    }).then(({ data }) => setBreakdownData(data))
      .catch(e => toast.error(apiErrorMessage(e, "Failed to load breakdown")))
      .finally(() => setBreakdownLoading(false));
  }, [breakdownKey, campaign, activeWorkspaceId, cid]);

  const toggleAdset = async (as) => {
    setExpanded(x => ({ ...x, [as.id]: !x[as.id] }));
    if (!adsByAdset[as.id]) {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/adsets/${as.id}/ads`);
        setAdsByAdset(x => ({ ...x, [as.id]: data.ads || [] }));
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load ads")); }
    }
  };

  const pickAd = async (adId) => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/ads/${adId}`);
      setSelectedAd(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load ad")); }
  };

  const toggleAdStatus = async (ad) => {
    const next = ad.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    try {
      await api.post(`/perf/ads/${ad.id}/status`, { status: next });
      toast.success(`Ad ${next.toLowerCase()}`);
      // Refresh containing adset's ads
      const asId = ad.adset_id;
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/perf/adsets/${asId}/ads`);
      setAdsByAdset(x => ({ ...x, [asId]: data.ads || [] }));
      if (selectedAd?.ad?.id === ad.id) pickAd(ad.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Toggle failed")); }
  };

  const toggleAdsetStatus = async (as) => {
    const next = as.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    try {
      await api.post(`/perf/adsets/${as.id}/status`, { status: next });
      toast.success(`Adset ${next.toLowerCase()}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Toggle failed")); }
  };

  if (!campaign) return <div className="p-6 text-sm text-muted-foreground">Loading campaign…</div>;

  return (
    <div className="flex flex-col h-full" data-testid="perf-campaign-detail-page">
      <PageHeader
        eyebrow="Performance · Campaign"
        title={campaign.name}
        description={`${campaign.objective || ""} · Daily budget ${fmtMoney(campaign.daily_budget)} · ${campaign.status}`}
        actions={
          <Button variant="outline" className="rounded-none h-9" onClick={() => navigate("/app/performance/campaigns")} data-testid="perf-detail-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to campaigns
          </Button>
        }
      />

      <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-10 py-4 space-y-4">
        {/* Adsets ↔ Ads ↔ Creative panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: adsets tree */}
          <div className="lg:col-span-2 border overflow-hidden">
            <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
              Adsets · {adsets.length}
            </div>
            {adsets.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">
                No adsets yet. Re-sync your connection to seed demo hierarchy.
              </div>
            )}
            <div className="divide-y">
              {adsets.map(as => (
                <div key={as.id} data-testid={`perf-adset-${as.id}`}>
                  <button onClick={() => toggleAdset(as)}
                    className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-3">
                    {expanded[as.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{as.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {as.targeting_summary || "—"} · {as.optimization_goal}
                      </div>
                    </div>
                    <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-widest border ${as.status === "ACTIVE" ? "border-emerald-400 text-emerald-800 bg-emerald-50" : "border-neutral-300 text-neutral-500 bg-neutral-50"}`}>{as.status}</span>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] text-muted-foreground">Spend 7d</div>
                      <div className="font-mono text-xs">{fmtMoney(as.spend_7d)}</div>
                    </div>
                    <span
                      role="button"
                      onClick={e => { e.stopPropagation(); toggleAdsetStatus(as); }}
                      className="p-1 text-muted-foreground hover:text-foreground"
                      title={as.status === "ACTIVE" ? "Pause adset" : "Resume adset"}
                      data-testid={`perf-adset-toggle-${as.id}`}
                    >
                      {as.status === "ACTIVE" ? <PauseCircle className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
                    </span>
                  </button>
                  {expanded[as.id] && (
                    <div className="bg-neutral-50 px-3 py-2 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(adsByAdset[as.id] || []).map(ad => (
                          <button key={ad.id}
                            onClick={() => pickAd(ad.id)}
                            className={`border bg-white p-2 flex items-center gap-2 hover:border-foreground text-left ${selectedAd?.ad?.id === ad.id ? "border-foreground shadow-sm" : ""}`}
                            data-testid={`perf-ad-${ad.id}`}
                          >
                            {ad.creative_thumbnail
                              ? <img src={ad.creative_thumbnail} alt="" className="w-10 h-10 object-cover" />
                              : <div className="w-10 h-10 bg-neutral-200 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-neutral-500" /></div>}
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold truncate">{ad.name}</div>
                              <div className="text-[10px] text-muted-foreground truncate">
                                {ad.creative_cta && <span>{ad.creative_cta.replace(/_/g, " ")} · </span>}
                                Spend 7d {fmtMoney(ad.spend_7d)}
                              </div>
                            </div>
                            <span
                              role="button"
                              onClick={e => { e.stopPropagation(); toggleAdStatus(ad); }}
                              className="p-1 text-muted-foreground hover:text-foreground"
                              title={ad.status === "ACTIVE" ? "Pause ad" : "Resume ad"}
                              data-testid={`perf-ad-toggle-${ad.id}`}
                            >
                              {ad.status === "ACTIVE" ? <PauseCircle className="w-3.5 h-3.5" /> : <PlayCircle className="w-3.5 h-3.5" />}
                            </span>
                          </button>
                        ))}
                        {(adsByAdset[as.id] || []).length === 0 && (
                          <div className="col-span-2 text-xs text-muted-foreground py-2 text-center">No ads under this adset yet.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: selected ad → preview + fatigue */}
          <div className="border overflow-hidden">
            <div className="px-3 py-2 border-b bg-neutral-50 text-[10px] uppercase tracking-widest text-muted-foreground">
              Ad preview
            </div>
            <div className="p-3 space-y-3 min-h-[240px]" data-testid="perf-ad-preview-panel">
              {!selectedAd && (
                <div className="text-sm text-muted-foreground text-center py-8">
                  <Sparkles className="w-6 h-6 text-neutral-400 mx-auto mb-2" />
                  Click an ad on the left to see its creative, preview, and fatigue signal.
                </div>
              )}
              {selectedAd?.fatigue && (
                <div className="border border-amber-400 bg-amber-50 text-amber-900 p-2 text-xs" data-testid="perf-ad-fatigue-banner">
                  <div className="font-semibold flex items-center gap-1"><Flame className="w-3.5 h-3.5" /> Creative fatigue</div>
                  <div>{selectedAd.fatigue.message}</div>
                </div>
              )}
              {selectedAd?.preview_html && (
                <div className="border" dangerouslySetInnerHTML={{ __html: selectedAd.preview_html }} data-testid="perf-ad-preview-html" />
              )}
              {selectedAd?.ad && (
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>Status: <b className="text-foreground">{selectedAd.ad.status}</b> · Effective: {selectedAd.ad.effective_status}</div>
                  <div>Spend 30d: <b className="text-foreground">{fmtMoney(selectedAd.ad.spend_7d)}</b> · CTR: {fmtPct(selectedAd.ad.ctr_7d)} · CPL: {fmtMoney(selectedAd.ad.cpl_7d)}</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Breakdown insights */}
        <div className="border">
          <div className="px-3 py-2 border-b bg-neutral-50 flex items-center gap-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2">Breakdown</span>
            {BREAKDOWNS.map(b => {
              const Icon = b.icon;
              return (
                <button key={b.key} onClick={() => setBreakdownKey(b.key)}
                  data-testid={`perf-breakdown-tab-${b.key}`}
                  className={`text-xs px-3 py-1 border-b-2 flex items-center gap-1.5 ${breakdownKey === b.key ? "border-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="w-3 h-3" /> {b.label}
                </button>
              );
            })}
          </div>
          <div className="p-3">
            {breakdownLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!breakdownLoading && breakdownData && (
              <BreakdownTable data={breakdownData} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BreakdownTable({ data }) {
  const rows = data?.rows || [];
  const maxSpend = useMemo(() => Math.max(1, ...rows.map(r => r.spend || 0)), [rows]);
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-2">
        Range: <b>{data.date_from}</b> → <b>{data.date_to}</b> · Total spend:
        <b className="ml-1 text-foreground">{fmtMoney(data.totals?.spend)}</b> ·
        Impr {fmtInt(data.totals?.impressions)} · Clicks {fmtInt(data.totals?.clicks)}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="perf-breakdown-table">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
            <tr className="border-b">
              <th className="text-left py-1.5 pr-2">Segment</th>
              <th className="text-right py-1.5 pr-2">Spend</th>
              <th className="text-right py-1.5 pr-2">Impr</th>
              <th className="text-right py-1.5 pr-2">Clicks</th>
              <th className="text-right py-1.5 pr-2">CTR</th>
              <th className="text-right py-1.5 pr-2">CPC</th>
              <th className="text-right py-1.5 pr-2">CPM</th>
              <th className="text-right py-1.5 pr-2">Conv</th>
              <th className="text-right py-1.5 pr-2">CPL</th>
              <th className="text-left py-1.5 pl-3 w-40">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.breakdown_value} className="border-b hover:bg-neutral-50" data-testid={`perf-breakdown-row-${i}`}>
                <td className="py-1 pr-2 font-medium">{r.breakdown_value}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtMoney(r.spend)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtInt(r.impressions)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtInt(r.clicks)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtPct(r.ctr)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtMoney(r.cpc)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtMoney(r.cpm)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtInt(r.conversions)}</td>
                <td className="py-1 pr-2 text-right font-mono">{fmtMoney(r.cpl)}</td>
                <td className="py-1 pl-3">
                  <div className="h-2 bg-neutral-200 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-foreground" style={{ width: `${(r.spend / maxSpend) * 100}%` }} />
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="py-4 text-center text-muted-foreground">No data for this breakdown yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
